import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { MODULES, moduleByKey } from "../modules";
import api from "../lib/api";

const WsCtx = createContext(null);
export const useWorkspace = () => useContext(WsCtx);

export function WorkspaceProvider({ children }) {
  const dash = moduleByKey("dashboard");
  const [tabs, setTabs] = useState([dash]);
  const [active, setActive] = useState("dashboard");
  const [resumeDraftId, setResumeDraftId] = useState(null);
  const [drill, setDrill] = useState(null);
  const [fieldTab, setFieldTab] = useState("pending");
  // project === null  ->  "All Projects" (aggregated across authorized projects)
  const [ctx, setCtx] = useState({ project: null, store: null });
  const [options, setOptions] = useState({ projects: [], stores: [], is_admin: false });
  const [dataVersion, setDataVersion] = useState(0);

  const refreshOptions = useCallback(() => {
    return api.get("/context/options").then((r) => {
      setOptions(r.data || { projects: [], stores: [] });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/context/options").then((r) => {
      const data = r.data || { projects: [], stores: [] };
      setOptions(data);
      // Auto-select the only authorized project; otherwise default to All Projects.
      if ((data.projects || []).length === 1) setCtx({ project: data.projects[0], store: null });
    }).catch(() => {});
  }, []);

  // Manual, per-section refresh: re-pull shared project/store lists and bump a version
  // that data-loading effects depend on — so a section reloads its data without a full ERP reload.
  const refreshWorkspace = useCallback(async () => {
    await refreshOptions();
    setDataVersion((v) => v + 1);
  }, [refreshOptions]);

  const openTab = useCallback((key) => {
    const m = moduleByKey(key);
    if (!m) return;
    setTabs((t) => (t.find((x) => x.key === key) ? t : [...t, m]));
    setActive(key);
  }, []);

  const closeTab = useCallback((key) => {
    if (key === "dashboard") return;
    setTabs((t) => t.filter((x) => x.key !== key));
    setActive((a) => (a === key ? "dashboard" : a));
  }, []);

  // No cross-session persistence: a fresh login always starts from the predictable default.
  const setContext = useCallback((project, store) => setCtx({ project: project || null, store: store || null }), []);

  const resumeDraft = useCallback((id) => { setResumeDraftId(id); openTab("data-entry"); }, [openTab]);
  const clearResumeDraft = useCallback(() => setResumeDraftId(null), []);

  // Drill-down: carry a filter payload to a target module and open it.
  const drillDown = useCallback((key, payload) => { setDrill({ key, ...(payload || {}) }); openTab(key); }, [openTab]);
  const clearDrill = useCallback(() => setDrill(null), []);

  // Mobile: open the Field Workspace focused on a specific lifecycle tab.
  const openFieldTab = useCallback((t) => { setFieldTab(t || "pending"); openTab("field-workspace"); }, [openTab]);

  return (
    <WsCtx.Provider value={{ tabs, active, setActive, openTab, closeTab, ctx, setContext, options, refreshWorkspace, refreshOptions, dataVersion, MODULES, resumeDraftId, resumeDraft, clearResumeDraft, drill, drillDown, clearDrill, fieldTab, setFieldTab, openFieldTab }}>
      {children}
    </WsCtx.Provider>
  );
}
